import { useDispatch, useSelector } from 'react-redux';
import { Message } from '../interface/chat';
import { RootState } from '../store/reducers';
import {
  addMessage,
  removeMessage,
  setChatHistory,
  setChatId,
  setError,
  setLoading,
  updateMessage,
  setCurrentPage,
  setTotalPages,
  clearChatHistory,
  setHasUnprocessedMessage,
  setVoiceMode,
  updateMessageId,
  setUnprocessedMessage,
  setIsProcessingMessage,
  setShowTypingIndicator,
  setIsStreaming,
  setStreamingText,
  setConversationStreamingState,
} from '../store/reducers/chat.reducer';

export const useChatStore = () => {
  const dispatch = useDispatch();
  const {
    voiceMode,
    chatHistory,
    loading: chatLoading,
    error: chatError,
    chatId,
    hasUnprocessedMessage,
    currentPage,
    totalPages,
    isProcessingMessage,
    unprocessedMessage,
    showTypingIndicator,
    isStreaming,
    streamingText,
  } = useSelector((state: RootState) => state.chat);

  // Direct dispatch methods for more flexibility
  const dispatchAction = (type: string, payload?: any) => {
    dispatch({ type, payload });
  };

  const addMessageRaw = (message: Message) => {
    dispatch(addMessage(message));
  };

  const updateMessageRaw = (message: Message) => {
    dispatch(updateMessage(message));
  };
  const updateMessageIdRaw = (message: Message) => {
    dispatch(updateMessageId(message));
  };

  return {
    // State
    voiceMode,
    chatHistory,
    chatLoading,
    chatError,
    chatId,
    hasUnprocessedMessage,
    currentPage,
    totalPages,
    unprocessedMessage,
    isProcessingMessage,
    showTypingIndicator,
    isStreaming,
    streamingText,
    // Dispatchers for actions
    removeMessage: (messageId: string) => dispatch(removeMessage(messageId)),
    updateMessage: (message: Message) => dispatch(updateMessage(message)),
    setChatHistory: (messages: Message[]) => dispatch(setChatHistory(messages)),

    setChatId: (id: string | null) => dispatch(setChatId(id)),
    setError: (error: string | null) => dispatch(setError(error)),
    setLoading: (loading: boolean) => dispatch(setLoading(loading)),
    setCurrentPage: (page: number) => dispatch(setCurrentPage(page)),
    setTotalPages: (total: number) => dispatch(setTotalPages(total)),
    clearChatHistory: () => dispatch(clearChatHistory()),
    setUnprocessedMessage: (message: Message | null) =>
      dispatch(setUnprocessedMessage(message)),
    setIsProcessingMessage: (value: boolean) =>
      dispatch(setIsProcessingMessage(value)),
    setHasUnprocessedMessage: (value: boolean) =>
      dispatch(setHasUnprocessedMessage(value)),
    setVoiceMode: (value: boolean) => dispatch(setVoiceMode(value)),
    // Direct dispatch for more complex scenarios
    dispatchAction,
    addMessageRaw,
    updateMessageRaw,
    updateMessageIdRaw,
    setShowTypingIndicator: (value: boolean) =>
      dispatch(setShowTypingIndicator(value)),
    setIsStreaming: (value: boolean) => dispatch(setIsStreaming(value)),
    setStreamingText: (value: string) => dispatch(setStreamingText(value)),
    setConversationStreamingState: (payload: {
      conversationId: string;
      isStreaming?: boolean;
      streamingText?: string;
      showTypingIndicator?: boolean;
      isProcessingMessage?: boolean;
      unprocessedMessage?: Message | null;
    }) => dispatch(setConversationStreamingState(payload)),
  };
};
